import {
    AttachMoneyRounded as AttachMoneyRoundedIcon,
    Checklist as ChecklistIcon,
    DriveFileMoveOutlined as DriveFileMoveOutlinedIcon,
    MoneyOffRounded as MoneyOffRoundedIcon,
    ScheduleOutlined as ScheduleOutlinedIcon,
} from 'components/icons';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import SwitchMatterModal, { SwitchMatterModalResult } from 'components/modals/invoices/SwitchMatterModal.tsx';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util.tsx';
import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { ListViewsPageContext, TIMECARD_VIEWS } from 'components/tables/list-table/filter-bar/builtInViews';
import { useRecordTabBuiltIn } from 'components/tables/list-table/filter-bar/ListViewsScope';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { tableLoadingProps } from 'components/tables/list-table/tableLoadingProps';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth.ts';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { QueryParams } from 'store/api/common/types';
import { useStartTimecardExportMutation } from 'store/api/exports';
import {
    useGetTimecardGroupTotalsQuery,
    useGetTimecardsTotalsQuery,
    useLazyGetTimecardsCountQuery,
    useLazyGetTimecardsWithFiltersQuery,
    useSwitchTimecardsMatterMutation,
    useUpdateTimecardsBillabilityMutation,
} from 'store/api/timecards';
import { EXPORT_CONFIG } from 'types/exports';
import {
    SwitchTimecardsMatter,
    TimecardListItemData,
    TimecardStatusType,
    UpdateTimecardsBillability,
} from 'types/timecards';
import { successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { logError } from 'utils/monitoring';
import { PaginatedFieldPassiveFilter } from 'utils/filterUtil.ts';
import { t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { ModalArgs } from 'utils/useExcelExport.ts';
import TimecardExportVersionModal from './components/TimecardExportVersionModal.tsx';
import TimecardsBulkSelectModal from './components/TimecardsBulkSelectModal.tsx';
import { getTimecardListColumns } from './list/getTimecardListColumns.tsx';
import { GroupBySelector } from 'components/GroupBySelector';
import { TimecardGroupRibbon } from './list/TimecardGroupRibbon.tsx';
import { canOpenTimecard } from './list/TimecardRowActions.tsx';
import {
    composeTimecardSort,
    DEFAULT_TIMECARD_GROUP_BY,
    formatGroupDate,
    GROUP_BY_FILTER_GROUP,
    TIMECARD_GROUP_BY_LABEL,
    TIMECARD_GROUP_BY_OPTIONS,
    timecardGroupKeyOf,
} from './list/timecardGroupBy.ts';
import { useViewParam } from 'hooks/useViewParam';
import { VIEW_PRESERVED_PARAM_KEYS } from 'utils/viewParam';
import { groupTimecards } from './TimecardList';
import {
    type FilterGroup,
    type FilterId,
    TIMECARD_LIST_CLASSIFIERS,
    useTimecardFilterConfig,
} from './useTimecardFilterConfig';
import { useTimecardViews } from './useTimecardViews';
import { isTimecardSelectable } from './utils/timecardUtils.ts';

/** A bordered strip of icon buttons above the selectable list. */
const BULK_ACTION_GROUP_CLASS = 'bg-paper text-fg border-divider inline-flex w-fit gap-1 rounded-[8px] border p-1';
const BULK_ACTION_BUTTON_CLASS = cn(
    'text-fg hover:bg-grey-900/4 hover:text-fg disabled:text-grey-300 size-[30px] rounded-[4px] disabled:opacity-100',
    GLYPH_OWN_SIZE
);

const TIMECARD_VIEW_IDS = TIMECARD_VIEWS.map(({ id }) => id);

const getTimecardHref = (timecard: TimecardListItemData) =>
    canOpenTimecard(timecard) ? `/timecards/${timecard.id}` : undefined;

/** Which list surface this instance is, and so which column layout and grouping it remembers. */
type TimecardListSurface = 'main' | 'matter' | 'contact' | 'project';

const TimecardListWithFilters = ({
    passiveFilters = '',
    disableUrlState = false,
    disableMainFilters = false,
    disableExport = false,
    matterId,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
    showTotalsByDefault = false,
    tabType: tabTypeProp,
    surface = 'main',
}: {
    passiveFilters?: string;
    disableUrlState?: boolean;
    disableMainFilters?: boolean;
    disableExport?: boolean;
    matterId?: string;
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
    /** Whether the totals row starts on. The "Show totals" toggle can dismiss or restore it either
     *  way; a surface whose fees say nothing (an internal client's own cases) starts it off. */
    showTotalsByDefault?: boolean;
    tabType?: 'main' | 'draft';
    /** Defaults to the standalone list; every embedded surface names itself so their column
     *  layouts and groupings stay independent. The MAIN and DRAFT tabs are one surface — same
     *  table, so one remembered layout. */
    surface?: TimecardListSurface;
}) => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: TIMECARD_LIST_CLASSIFIERS,
    });
    const formatName = useEmployeeNameFormatter();
    const dateFormat = useEffectiveDateFormat();

    const [fetchData, { data, currentData, isFetching, isLoading, isError }] = useLazyGetTimecardsWithFiltersQuery();
    const tableLoading = tableLoadingProps({ isLoading, isFetching, currentData });

    // Both timecard export variants hit the same backend export task (`useStartTimecardExportMutation`);
    // only the entity type differs, which selects the mapper/writer used when the file is downloaded.
    const { isBusy: isDetailedExportFetching, triggerManualDownload: triggerDetailedExport } = useAsyncExport(
        useStartTimecardExportMutation,
        {
            entityType: 'TIMECARD',
            entityName: t('timecards.export.entity-name'),
            fileName: EXPORT_CONFIG.TIMECARD.fileName,
        }
    );
    const { isBusy: isCompactExportFetching, triggerManualDownload: triggerCompactExport } = useAsyncExport(
        useStartTimecardExportMutation,
        {
            entityType: 'TIMECARD_COMPACT',
            entityName: t('timecards.export.entity-name'),
            fileName: EXPORT_CONFIG.TIMECARD_COMPACT.fileName,
        }
    );
    const isExportDataFetching = isDetailedExportFetching || isCompactExportFetching;

    const hasPermissionToExportTimecards = useHasPermissions(['EXPORT.TIMECARDS']);
    const navigateWithReturn = useNavigateWithReturnState();
    const { user } = useAuth();
    const userId = user?.id;

    // In a record's tab in the new design, MAIN and DRAFT are the views row's built-in views, named by the URL.
    const recordViews = useIsNewDesign() && surface !== 'main' && !disableUrlState;
    const viewTab = useRecordTabBuiltIn(
        recordViews ? 'timecards' : undefined,
        TIMECARD_VIEW_IDS,
        tabTypeProp ?? 'main'
    );
    const tabType = recordViews ? (viewTab as typeof tabTypeProp) : tabTypeProp;
    const timecardViews = useTimecardViews();

    const initialSearchParams = useMemo(
        () => (tabType !== 'draft' && userId ? `qf=id=ismynondrafttimecard=true` : undefined),
        [tabType, userId]
    );

    const { openModal, closeModal } = useModal();
    const selectableTimecards = matterId !== undefined;

    // ---- bulk actions (a case tab only) ----
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [updateTimecardsMatter] = useSwitchTimecardsMatterMutation();
    const [updateTimecardsBillability, { isLoading: isBillabilityUpdating }] = useUpdateTimecardsBillabilityMutation();

    const handleMatterChange = (value: SwitchMatterModalResult) => {
        if (!matterId || !value.matter?.id) {
            return;
        }

        const body: SwitchTimecardsMatter = {
            currentMatterId: Number(matterId),
            newMatterId: value.matter?.id,
            newMatterAssignmentId: value?.matterAssignment?.id || null,
            timecardIds: selectedIds,
        };
        updateTimecardsMatter(body)
            .unwrap()
            .then(() => {
                // The moved timecards leave this list, so their ids must leave the selection with
                // them — a later billability action would otherwise submit them against this case.
                setSelectedIds([]);
                successAlert(t('matters.timecard-is-moved'));
            })
            .catch(logError);
        closeModal();
    };

    const handleBillabilityChange = (isNonBillable: boolean) => {
        if (!matterId) {
            return;
        }

        const body: UpdateTimecardsBillability = {
            matterId: Number(matterId),
            timecardIds: selectedIds,
            isNonBillable,
        };
        updateTimecardsBillability(body)
            .unwrap()
            .then(() => {
                setSelectedIds([]);
                successAlert(
                    t(isNonBillable ? 'matters.timecards-marked-non-billable' : 'matters.timecards-marked-billable')
                );
            })
            .catch(logError);
    };

    const openTimecardsUpdateModal = (): void => {
        openModal(
            <SwitchMatterModal
                onSubmit={handleMatterChange}
                onCancel={closeModal}
                titleId={'invoices.move-to-another-case'}
                entityIds={new Set(selectedIds)}
                entitySingular={t('timecards.entity-name-singular')}
                entityPlural={t('timecards.entity-name-plural')}
                showAssignmentSelect={true}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const onBulkSelect = (isToSelect: boolean, timecardIds: number[]) => {
        setSelectedIds((current) =>
            isToSelect
                ? [...current, ...timecardIds.filter((id) => !current.includes(id))]
                : current.filter((id) => !timecardIds.includes(id))
        );
    };

    const handleBulkSelect = () => {
        openModal(
            <TimecardsBulkSelectModal
                onBulkSelect={(ids) => onBulkSelect(true, ids)}
                onBulkUnselect={(ids) => onBulkSelect(false, ids)}
                onClose={closeModal}
                timecards={
                    data?.content.map((tc) => ({
                        ...tc,
                        isSelectable: isTimecardSelectable(tc),
                    })) || []
                }
            />
        );
    };

    const toggleSelected = useCallback((timecardId: number, isSelected: boolean) => {
        setSelectedIds((current) =>
            isSelected ? [...current, timecardId] : current.filter((id) => id !== timecardId)
        );
    }, []);

    // Hand the selection to the chrome, which drops any id whose row is no longer in the result —
    // a filter or search that hides a selected timecard must not leave it staged for a bulk action
    // the user can no longer see.
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const handleSelectionChange = useCallback((visibleSelection: Set<number>) => {
        setSelectedIds([...visibleSelection]);
    }, []);

    // ---- grouping ----
    // A surface that fixes a grouping (one case, one client) drops it from the control: every row
    // would land in the same group, so the option could only ever be a no-op.
    const groupByOptions = useMemo(
        () =>
            TIMECARD_GROUP_BY_OPTIONS.filter((option) => {
                const filterGroup = GROUP_BY_FILTER_GROUP[option];
                return !filterGroup || !disabledGroups?.includes(filterGroup);
            }),
        [disabledGroups]
    );
    const columnConfigId = `timecards-${surface}`;
    const [groupBy, setGroupBy] = useViewParam(columnConfigId, groupByOptions, DEFAULT_TIMECARD_GROUP_BY);

    // The grouping decides the PRIMARY sort, so groups arrive contiguous instead of being cut up by
    // the 50-row page boundary; the user's column sort then orders rows within each group. A new
    // identity here also refetches from page 0, which is what a changed grouping needs.
    const composeSort = useCallback((columnSort: string) => composeTimecardSort(groupBy, columnSort), [groupBy]);

    // The active RSQL filter, as the list table assembles it from the user's main/quick/passive filters
    // and reports it. `undefined` until the first fetch reports one, which is what keeps the group-totals
    // request from firing once against an empty filter and again against the real one.
    const [activeFilter, setActiveFilter] = useState<string | undefined>(undefined);

    // The groups the loaded rows belong to, in row order and without a keyless one (a row with no author
    // has no group to ask the server for). Off `currentData`, not `data`: while a new filter or grouping
    // is in flight the latter still holds the old rows, whose keys would be requested under the new filter.
    const loadedGroupKeys = useMemo(() => {
        if (groupBy === 'none') {
            return [];
        }
        const keyOf = timecardGroupKeyOf(groupBy);
        return [...new Set((currentData?.content ?? []).map(keyOf))].filter((key) => key !== '');
    }, [groupBy, currentData?.content]);

    // A group's totals cover the whole filtered set, not the 50-row page the reader has scrolled to: the
    // rows in hand are a fraction of a long group, so summing them states a figure that moves on scroll.
    // Asked for the groups on screen only, though — every group of a firm-wide list would be every
    // timecard of the tenant, for a page that shows a handful of days. `currentData` and not `data`: the
    // latter holds the previous filter's or grouping's answer while the next one is in flight, which
    // would apply one grouping's totals to another's rows.
    const groupTotalsArgs = useMemo(
        () => ({ filters: activeFilter ?? '', groupBy: groupBy.toUpperCase(), groupKeys: loadedGroupKeys }),
        [activeFilter, groupBy, loadedGroupKeys]
    );
    const { currentData: groupTotals, isError: isGroupTotalsError } = useGetTimecardGroupTotalsQuery(groupTotalsArgs, {
        skip: groupBy === 'none' || activeFilter === undefined || loadedGroupKeys.length === 0,
    });

    // Each group carries whether its own totals are in hand yet. Where they are not, the ribbon shows a
    // placeholder rather than the loaded-row sums underneath: a figure that grows as the reader scrolls
    // reports how far they have got, not what the group holds.
    //
    // A failed request is the exception, and falls back to those sums. The placeholder is only honest while
    // the real figure is still coming; once it cannot arrive, an approximate number beats a dash that never
    // resolves.
    const groups = useMemo(() => {
        if (groupBy === 'none') {
            return [];
        }
        const totalsByKey = new Map((groupTotals ?? []).map((totals) => [totals.groupKey, totals]));
        return [...groupTimecards(data?.content ?? [], timecardGroupKeyOf(groupBy))].map(([key, group]) => {
            const totals = totalsByKey.get(key);
            return totals
                ? {
                      ...group,
                      totalsPending: false,
                      loggedTimeSumInHours: totals.loggedTimeInHours,
                      billableTimeSumInHours: totals.billedTimeInHours,
                      totalAmountInBaseCurrency: totals.totalAmountInBaseCurrency,
                      feesByCurrency: new Map(Object.entries(totals.feesByCurrency)),
                  }
                : // A keyless group is never asked for, so its ribbon shows the rows' own sums from the start.
                  { ...group, totalsPending: !isGroupTotalsError && key !== '' };
        });
    }, [groupBy, data?.content, groupTotals, isGroupTotalsError]);

    // Its group's ribbon for EVERY row, keyed by row id so it survives the chrome's deferred row set
    // (which lags the response by a render). Every row and not just the group's first, because the
    // ribbon is rendered twice from the one node: in flow above the row that opens the group, and
    // frozen under the header for whichever row the reader has scrolled to — so the group on show
    // stays named while its rows do.
    const { ribbonByRowId, groupOpenerIds } = useMemo(() => {
        const byRowId = new Map<number, JSX.Element>();
        const openerIds = new Set<number>();
        groups.forEach((group) => {
            const first = group.timecards[0];
            if (!first) return;
            const { matter, employee } = first;
            const title =
                groupBy === 'date'
                    ? formatGroupDate(first.timecardDate, dateFormat)
                    : groupBy === 'case'
                      ? `${matter.caseNumber} ${matter.name}`
                      : groupBy === 'client'
                        ? (matter.clientParty?.name ?? '')
                        : formatName(employee);
            const subtitle =
                groupBy === 'case'
                    ? matter.clientParty?.name
                    : groupBy === 'author'
                      ? (employee?.latestEmployment?.officeName ?? undefined)
                      : undefined;
            const ribbon = (
                <TimecardGroupRibbon
                    title={title}
                    subtitle={subtitle}
                    group={group}
                    totalsPending={group.totalsPending}
                />
            );
            openerIds.add(first.id);
            group.timecards.forEach((timecard) => byRowId.set(timecard.id, ribbon));
        });
        return { ribbonByRowId: byRowId, groupOpenerIds: openerIds };
    }, [groups, groupBy, dateFormat, formatName]);

    const renderRowLeadIn = useCallback(
        (timecard: TimecardListItemData) =>
            (groupOpenerIds.has(timecard.id) ? ribbonByRowId.get(timecard.id) : null) ?? null,
        [ribbonByRowId, groupOpenerIds]
    );

    const renderStickyLeadIn = useCallback(
        (timecard: TimecardListItemData) => ribbonByRowId.get(timecard.id) ?? null,
        [ribbonByRowId]
    );

    // ---- columns ----
    const columns = useMemo(
        () =>
            getTimecardListColumns({
                classifiers,
                formatName,
                selection: selectableTimecards ? { selectedIds, onToggle: toggleSelected } : undefined,
                isCaseScoped: matterId !== undefined,
            }),
        [classifiers, formatName, selectableTimecards, selectedIds, toggleSelected, matterId]
    );

    const bulkActions = selectableTimecards ? (
        <div className="flex justify-end">
            <div className={cn(BULK_ACTION_GROUP_CLASS, 'mx-2')}>
                <Tooltip content={t('invoices.tooltips.bulk-selection')} side="bottom">
                    <span>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className={BULK_ACTION_BUTTON_CLASS}
                            aria-label={t('invoices.tooltips.bulk-selection')}
                            onClick={handleBulkSelect}
                        >
                            <ChecklistIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
            </div>
            <div className={BULK_ACTION_GROUP_CLASS}>
                <Tooltip content={t('matters.tooltips.mark-as-non-billable')} side="bottom">
                    <span>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className={BULK_ACTION_BUTTON_CLASS}
                            aria-label={t('matters.tooltips.mark-as-non-billable')}
                            disabled={isBillabilityUpdating || !selectedIds.length}
                            onClick={() => handleBillabilityChange(true)}
                        >
                            <MoneyOffRoundedIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip content={t('matters.tooltips.mark-as-billable')} side="bottom">
                    <span>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className={BULK_ACTION_BUTTON_CLASS}
                            aria-label={t('matters.tooltips.mark-as-billable')}
                            disabled={isBillabilityUpdating || !selectedIds.length}
                            onClick={() => handleBillabilityChange(false)}
                        >
                            <AttachMoneyRoundedIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip content={t('matters.tooltips.move-to-another-case')} side="bottom">
                    <span>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className={BULK_ACTION_BUTTON_CLASS}
                            aria-label={t('matters.tooltips.move-to-another-case')}
                            disabled={!selectedIds.length}
                            onClick={openTimecardsUpdateModal}
                        >
                            <DriveFileMoveOutlinedIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
            </div>
        </div>
    ) : undefined;

    const filterConfig = useTimecardFilterConfig({
        userId,
        disableMainFilters,
        passiveFilters,
        classifiers,
        disabledGroups,
        disabledFilterIds,
        paginatedFieldPassiveFilters,
        tabType,
    });

    const getExportConfirmationModalArgsCallback = useMemo<ModalArgs<TimecardListItemData[]>>(
        () => ({
            shouldOpen: (timecards) =>
                !!timecards?.find((tc: TimecardListItemData) => tc.timecardStatusTypeCode === TimecardStatusType.DRAFT),
            openModal: (callback: () => void) => {
                openModal(
                    <ConfirmationModal
                        onConfirm={() => {
                            // Pop the confirmation first so the version chooser opened by `callback`
                            // (openExportVersionModal) stays on top of the modal stack.
                            closeModal();
                            if (callback) {
                                callback();
                            }
                        }}
                        onCancel={closeModal}
                        content={
                            <p className="text-body1">
                                <FormattedMessage id="timecards.about-to-export-draft-timecards" />
                            </p>
                        }
                        confirmId="yes-i-am-sure"
                    />,
                    closeModal
                );
            },
        }),
        [closeModal, openModal]
    );

    // Export action opens a Compact / Detailed / Cancel chooser before starting the async export.
    // Both variants hit the same backend export task; only the downloaded file differs.
    const openExportVersionModal = useCallback(
        (args: QueryParams, elementCount: number, _modalArgs?: unknown, surface?: string): Promise<void> => {
            const startExport = (trigger: typeof triggerDetailedExport) => {
                closeModal();
                void trigger(args, elementCount, undefined, surface);
            };
            openModal(
                <TimecardExportVersionModal
                    onSelectCompact={() => startExport(triggerCompactExport)}
                    onSelectDetailed={() => startExport(triggerDetailedExport)}
                    onCancel={closeModal}
                />,
                closeModal
            );
            return Promise.resolve();
        },
        [closeModal, openModal, triggerCompactExport, triggerDetailedExport]
    );

    if (isLoadingClassifiers || (recordViews && !tabType)) {
        return <Loader />;
    }

    const list = (
        <ListTableWithFilters<TimecardListItemData>
            key={recordViews ? tabType : undefined}
            getRowHref={getTimecardHref}
            data={data}
            {...tableLoading}
            pluralTextId="timecards.timecards"
            singularTextId="timecards.timecard"
            isError={isError}
            // A case surface can say the list is scoped to that case, and duplicate the header's
            // create action in place — the header can be scrolled out of an independently scrolling tab.
            noEntriesId={matterId ? 'timecards.empty.matter-title' : 'timecards.no-timecards-found'}
            noEntriesBodyId={matterId ? 'timecards.empty.matter-body' : undefined}
            noEntriesIcon={ScheduleOutlinedIcon}
            noEntriesAction={
                matterId ? (
                    <ListCreateButton
                        permissions={['CREATE.TIMECARDS']}
                        variant="contained"
                        color="secondary"
                        onClick={() => navigateWithReturn(`/matter/${matterId}/timecards/new`)}
                        labelId="timecards.add-new-timecard"
                        newLabelId="new-entity.timecard"
                    />
                ) : undefined
            }
            fetchData={fetchData}
            composeSort={composeSort}
            fetchCount={useLazyGetTimecardsCountQuery}
            fetchTotals={useGetTimecardsTotalsQuery}
            // The aggregation covers the whole filtered set in one statement, so it carries no
            // record cap: the toggle only exists so the row can be dismissed.
            onDemandTotals
            defaultTotalsRowRequested={showTotalsByDefault}
            onFilterChange={setActiveFilter}
            filterConfig={filterConfig}
            rowsPerPage={50}
            columns={columns}
            columnConfigId={columnConfigId}
            preservedParamKeys={VIEW_PRESERVED_PARAM_KEYS}
            enableColumnPicker
            enableColumnReorder
            enableColumnResize
            renderRowLeadIn={renderRowLeadIn}
            renderStickyLeadIn={renderStickyLeadIn}
            scrollRestorationId={columnConfigId}
            quickFilterTrailing={
                <GroupBySelector
                    label={t('timecards.list.group-by')}
                    value={groupBy}
                    options={groupByOptions}
                    optionLabel={(option) => t(TIMECARD_GROUP_BY_LABEL[option])}
                    onChange={setGroupBy}
                />
            }
            extraHeaderComponent={bulkActions}
            selectedIds={selectableTimecards ? selectedIdSet : undefined}
            onSelectionChange={selectableTimecards ? handleSelectionChange : undefined}
            exportHookResults={
                disableExport || !hasPermissionToExportTimecards
                    ? undefined
                    : [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload: openExportVersionModal,
                          },
                      ]
            }
            exportAlertRequireConfirmation={getExportConfirmationModalArgsCallback}
            disableUrlState={disableUrlState}
            initialSearchParams={initialSearchParams}
        />
    );
    return recordViews && tabType ? (
        <ListViewsPageContext.Provider value={{ listKey: 'timecards', views: timecardViews, activeId: tabType }}>
            {list}
        </ListViewsPageContext.Provider>
    ) : (
        list
    );
};

export default TimecardListWithFilters;
